'use client';

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/ui-kit/utils';

interface UnderlineTab {
  id: string;
  label: React.ReactNode;
}

interface UnderlineTabsProps {
  tabs: UnderlineTab[];
  activeTab: string;
  onTabChange: (id: string) => void;
  className?: string;
}

export function UnderlineTabs({ tabs, activeTab, onTabChange, className }: UnderlineTabsProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);
  const [animated, setAnimated] = useState(false);

  // Keyed by ids, not by the array: callers often pass a fresh one per render.
  const tabKey = tabs.map((tab) => tab.id).join('|');

  const measure = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector<HTMLButtonElement>(`[data-tab="${activeTab}"]`);
    if (!el) {
      setIndicator(null);
      return;
    }
    const left = el.offsetLeft;
    const width = el.offsetWidth;
    setIndicator((prev) =>
      prev && prev.left === left && prev.width === width ? prev : { left, width },
    );
  }, [activeTab]);

  // The active tab is not the only thing that moves the line: a tab that comes
  // or goes shifts the ones after it, and a label that changes ("Ready to
  // Claim (3)") or a late web font resizes them.
  // biome-ignore lint/correctness/useExhaustiveDependencies: tabKey re-measures when tabs come or go
  useLayoutEffect(() => {
    measure();
  }, [measure, tabKey]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: tabKey re-observes the new buttons
  useEffect(() => {
    const list = listRef.current;
    if (!list || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    for (const el of list.querySelectorAll('[data-tab]')) observer.observe(el);
    return () => observer.disconnect();
  }, [measure, tabKey]);

  useEffect(() => {
    if (indicator && !animated) {
      const id = requestAnimationFrame(() => setAnimated(true));
      return () => cancelAnimationFrame(id);
    }
  }, [indicator, animated]);

  return (
    <div ref={listRef} className={cn('relative flex items-center gap-5', className)}>
      {indicator && (
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-0 h-px bg-v2-text-primary"
          style={{
            transform: `translateX(${indicator.left}px)`,
            width: indicator.width,
            transition: animated
              ? 'transform 250ms ease-in-out, width 250ms ease-in-out'
              : undefined,
          }}
        />
      )}
      {tabs.map((tab) => {
        const isActive = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            type="button"
            data-tab={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={cn(
              'relative cursor-pointer pt-1 pb-1.5 text-[13px] font-medium leading-4 tracking-[-0.13px] whitespace-nowrap transition-colors',
              isActive
                ? 'text-v2-text-primary'
                : 'text-v2-text-secondary hover:text-v2-text-primary',
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
